import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  IconButton,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
  Snackbar,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  Person,
  Edit,
  Email,
  Phone,
  LocationOn,
  Code,
  Star,
  AttachMoney,
  ArrowBack,
  AddCircle,
  RemoveCircle,
  FileDownload,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import TabPanel from '../components/TabPanel';
import { engineersService } from '../services/supabase';
import { EngineerWithSkills } from '../types';
import { SkillSheetService } from '../services/skillSheetService';
import { getInitials, getStatusColor, getStatusText } from '../utils/helpers';
import { commonSkills, isValidSkill } from '../constants/skills';


const EngineerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [engineer, setEngineer] = useState<EngineerWithSkills | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [generatingSkillSheet, setGeneratingSkillSheet] = useState(false);
  
  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    bio: '',
    hourly_rate: 0,
    experience_years: 0,
    availability_status: 'available' as 'available' | 'busy' | 'unavailable',
    remote_work_preference: false
  });
  
  // Skills edit state
  const [editSkillsForm, setEditSkillsForm] = useState<{
    id?: number;
    skill_name: string;
    skill_level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
    years_experience?: number;
    isNew?: boolean;
    isDeleted?: boolean;
  }[]>([]);

  useEffect(() => {
    if (id) {
      loadEngineer(parseInt(id));
    }
  }, [id]);
  
  useEffect(() => {
    if (engineer) {
      setEditFormData({
        name: engineer.name,
        email: engineer.email || '',
        phone: engineer.phone || '',
        location: engineer.location || '',
        bio: engineer.bio || '',
        hourly_rate: engineer.hourly_rate || 0,
        experience_years: engineer.experience_years || 0,
        availability_status: engineer.availability_status,
        remote_work_preference: engineer.remote_work_preference
      });
      setEditSkillsForm(engineer.skills.map(skill => ({
        id: skill.id,
        skill_name: skill.skill_name,
        skill_level: skill.skill_level,
        years_experience: skill.years_experience,
        isNew: false,
        isDeleted: false
      })));
    }
  }, [engineer]);

  const loadEngineer = async (engineerId: number) => {
    setLoading(true);
    const result = await engineersService.getById(engineerId);
    if (result.error) {
      setError(result.error);
    } else {
      setEngineer(result.data);
    }
    setLoading(false);
  };
  
  // Skills management functions for edit
  const addEditSkill = () => {
    setEditSkillsForm(prev => [...prev, {
      skill_name: '',
      skill_level: 'beginner',
      years_experience: 0,
      isNew: true,
      isDeleted: false
    }]);
  };

  const removeEditSkill = (index: number) => {
    setEditSkillsForm(prev => prev.map((skill, i) => 
      i === index 
        ? skill.isNew 
          ? { ...skill, isDeleted: true } // Mark new skills for deletion
          : { ...skill, isDeleted: !skill.isDeleted } // Toggle deletion for existing skills
        : skill
    ));
  };

  const updateEditSkill = (index: number, field: string, value: any) => {
    setEditSkillsForm(prev => prev.map((skill, i) => 
      i === index ? { ...skill, [field]: value } : skill
    ));
  };
  
  const handleGenerateSkillSheet = async () => {
    if (!engineer) return;
    
    setGeneratingSkillSheet(true);
    try {
      const result = await SkillSheetService.generateSkillSheet(engineer.id, {
        includePersonalInfo: true,
        includeProjectHistory: true,
        includeSkillDetails: true,
        templateStyle: 'standard'
      });
      
      if (result.error || !result.data) {
        setSnackbar({ 
          open: true, 
          message: result.error || 'スキルシートの生成に失敗しました', 
          severity: 'error' 
        });
        return;
      }
      
      // ファイルダウンロード
      const fileName = `${getInitials(engineer.name)}_スキルシート_${new Date().toISOString().split('T')[0]}`;
      SkillSheetService.downloadFile(result.data, fileName);
      
      setSnackbar({ 
        open: true, 
        message: 'スキルシートを生成しました', 
        severity: 'success' 
      });
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'スキルシートの生成に失敗しました', 
        severity: 'error' 
      });
    } finally {
      setGeneratingSkillSheet(false);
    }
  };

  const handleSaveEngineer = async () => {
    if (!engineer) return;
    
    try {
      // Update engineer basic info
      const updateResult = await engineersService.update(engineer.id, editFormData);
      if (updateResult.error) {
        setSnackbar({ open: true, message: 'エンジニア情報の更新に失敗しました', severity: 'error' });
        return;
      }
      
      // Handle skills updates
      const validSkills = editSkillsForm.filter(skill => !skill.isDeleted && skill.skill_name.trim() !== '' && isValidSkill(skill.skill_name));
      
      // 無効なスキルがあるかチェック
      const invalidSkills = editSkillsForm.filter(skill => !skill.isDeleted && skill.skill_name.trim() !== '' && !isValidSkill(skill.skill_name));
      if (invalidSkills.length > 0) {
        setSnackbar({ open: true, message: 'プログラミング言語・フレームワークを選択してください', severity: 'error' });
        return;
      }
      
      // スキル操作を並列実行（async-parallel ルール適用）
      const skillOperations: Promise<any>[] = [];

      // Delete removed skills
      const deletedSkills = editSkillsForm.filter(skill => skill.isDeleted && !skill.isNew && skill.id);
      for (const skill of deletedSkills) {
        skillOperations.push(engineersService.deleteSkill(skill.id!));
      }

      // Update existing skills and add new skills
      for (const skill of validSkills) {
        if (skill.isNew) {
          skillOperations.push(engineersService.addSkill(engineer.id, {
            skill_name: skill.skill_name,
            skill_level: skill.skill_level,
            years_experience: skill.years_experience || 0
          }));
        } else if (skill.id) {
          skillOperations.push(engineersService.updateSkill(skill.id, {
            skill_name: skill.skill_name,
            skill_level: skill.skill_level,
            years_experience: skill.years_experience || 0
          }));
        }
      }

      await Promise.all(skillOperations);
      
      setSnackbar({ open: true, message: 'エンジニア情報を更新しました', severity: 'success' });
      setOpenEditDialog(false);
      loadEngineer(engineer.id); // Refresh data
    } catch (error) {
      setSnackbar({ open: true, message: '更新に失敗しました', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  if (error || !engineer) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography color="error">エンジニアが見つかりません</Typography>
      </Box>
    );
  }




  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate('/engineers')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
          エンジニア詳細
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FileDownload />}
          onClick={handleGenerateSkillSheet}
          disabled={generatingSkillSheet}
          sx={{ mr: 2 }}
        >
          {generatingSkillSheet ? '生成中...' : 'スキルシート生成'}
        </Button>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => setOpenEditDialog(true)}
          sx={{ bgcolor: '#3498db' }}
        >
          編集
        </Button>
      </Box>

      {/* 基本情報カード */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr auto' }, gap: 3, alignItems: 'start' }}>
            {/* プロフィール画像・基本情報 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ width: 120, height: 120, bgcolor: '#3498db' }}>
                <Person sx={{ fontSize: 60 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {getInitials(engineer.name)}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {engineer.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{engineer.email}</Typography>
                    </Box>
                  )}
                  {engineer.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{engineer.phone}</Typography>
                    </Box>
                  )}
                  {engineer.location && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">{engineer.location}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* 詳細情報 */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                プロフィール
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  自己紹介
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, minHeight: '20px', whiteSpace: 'pre-wrap' }}>
                  {engineer.bio || '自己紹介が登録されていません'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    登録日
                  </Typography>
                  <Typography variant="body2">{new Date(engineer.created_at).toLocaleDateString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    更新日
                  </Typography>
                  <Typography variant="body2">{new Date(engineer.updated_at).toLocaleDateString()}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    リモートワーク
                  </Typography>
                  <Typography variant="body2">{engineer.remote_work_preference ? '希望あり' : '希望なし'}</Typography>
                </Box>
                {engineer.experience_years != null && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      経験年数
                    </Typography>
                    <Typography variant="body2">{engineer.experience_years}年</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ステータス・単価 */}
            <Box sx={{ textAlign: 'center' }}>
              <Chip
                label={getStatusText(engineer.availability_status)}
                color={getStatusColor(engineer.availability_status) as any}
                sx={{ fontWeight: 'bold', mb: 2 }}
              />
              {engineer.hourly_rate != null && (
                <>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    <AttachMoney sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    ¥{engineer.hourly_rate.toLocaleString()}/時間
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    希望時給
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* タブメニュー */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, value) => setTabValue(value)}>
          <Tab label="業務内容・使用言語等" />
          <Tab label="基本情報" />
        </Tabs>
      </Card>

      {/* 業務内容・使用言語等タブ */}
      <TabPanel value={tabValue} index={0} sx={{ py: 3 }}>
        <Box sx={{ display: 'grid', gap: 3 }}>
          {/* 業務内容・使用言語等一覧 */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                <Code sx={{ mr: 1, verticalAlign: 'middle' }} />
                業務内容・使用言語等
              </Typography>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {engineer.skills.map((skill) => (
                  <Box key={skill.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" sx={{ minWidth: 120 }}>
                      {skill.skill_name}
                    </Typography>
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(skill.years_experience || 0) * 20}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 40 }}>
                        {skill.years_experience || 0}年
                      </Typography>
                      <Chip
                        label={skill.skill_level}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* 追加情報 */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                <Star sx={{ mr: 1, verticalAlign: 'middle' }} />
                追加情報
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    ID
                  </Typography>
                  <Typography variant="body2">{engineer.id}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    リモートワーク希望
                  </Typography>
                  <Typography variant="body2">
                    {engineer.remote_work_preference ? 'あり' : 'なし'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* 基本情報タブ */}
      <TabPanel value={tabValue} index={1} sx={{ py: 3 }}>
        <Box sx={{ display: 'grid', gap: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                詳細情報
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                    稼働状況
                  </Typography>
                  <Chip
                    label={getStatusText(engineer.availability_status)}
                    color={getStatusColor(engineer.availability_status) as any}
                    sx={{ mb: 2 }}
                  />
                  
                  {engineer.experience_years != null && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        経験年数
                      </Typography>
                      <Typography variant="body1">{engineer.experience_years}年</Typography>
                    </Box>
                  )}

                  {engineer.hourly_rate != null && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        希望時給
                      </Typography>
                      <Typography variant="body1">¥{engineer.hourly_rate.toLocaleString()}/時間</Typography>
                    </Box>
                  )}
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                    リモートワーク希望
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {engineer.remote_work_preference ? '希望あり' : '希望なし'}
                  </Typography>
                  
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                    自己紹介
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                    {engineer.bio || '自己紹介が登録されていません'}
                  </Typography>
                  
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                    登録日
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(engineer.created_at).toLocaleDateString()}
                  </Typography>
                  
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                    最終更新
                  </Typography>
                  <Typography variant="body1">
                    {new Date(engineer.updated_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>


      {/* 編集ダイアログ */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>エンジニア情報編集</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <TextField 
              fullWidth 
              label="名前" 
              value={editFormData.name}
              onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField 
              fullWidth 
              label="メールアドレス" 
              value={editFormData.email}
              onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
            />
            <TextField 
              fullWidth 
              label="電話番号" 
              value={editFormData.phone}
              onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
            />
            <TextField 
              fullWidth 
              label="住所" 
              value={editFormData.location}
              onChange={(e) => setEditFormData(prev => ({ ...prev, location: e.target.value }))}
            />
            <TextField 
              fullWidth 
              label="経験年数" 
              type="number" 
              value={editFormData.experience_years || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
            />
            <TextField 
              fullWidth 
              label="希望時給（円/時間）" 
              type="number" 
              value={editFormData.hourly_rate || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, hourly_rate: parseInt(e.target.value) || 0 }))}
            />
            <FormControl fullWidth>
              <InputLabel>稼働状況</InputLabel>
              <Select
                value={editFormData.availability_status}
                label="稼働状況"
                onChange={(e) => setEditFormData(prev => ({ ...prev, availability_status: e.target.value as any }))}
              >
                <MenuItem value="available">稼働可能</MenuItem>
                <MenuItem value="busy">稼働中</MenuItem>
                <MenuItem value="unavailable">稼働不可</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editFormData.remote_work_preference}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, remote_work_preference: e.target.checked }))}
                />
              }
              label="リモートワーク希望"
            />
            <TextField
              fullWidth
              label="自己紹介"
              multiline
              rows={3}
              value={editFormData.bio}
              onChange={(e) => setEditFormData(prev => ({ ...prev, bio: e.target.value }))}
            />
            
            <Divider sx={{ my: 2 }} />
            
            {/* 業務内容・使用言語等セクション */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">業務内容・使用言語等</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddCircle />}
                  onClick={addEditSkill}
                  size="small"
                >
                  業務・言語追加
                </Button>
              </Box>
              
              {editSkillsForm.filter(skill => !skill.isDeleted).map((skill, index) => (
                <Box key={skill.id || `new-${index}`} sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '2fr 1fr 1fr auto', 
                  gap: 1, 
                  mb: 2, 
                  alignItems: 'center',
                  opacity: skill.isDeleted ? 0.5 : 1
                }}>
                  <Autocomplete
                    options={commonSkills}
                    value={skill.skill_name}
                    onChange={(event, newValue) => {
                      updateEditSkill(index, 'skill_name', newValue || '');
                    }}
                    disabled={skill.isDeleted}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="業務内容・使用言語"
                        placeholder="プログラミング言語・フレームワークを選択してください"
                        size="small"
                        error={skill.skill_name !== '' && !isValidSkill(skill.skill_name)}
                        helperText={skill.skill_name !== '' && !isValidSkill(skill.skill_name) ? 'プログラミング言語・フレームワークを選択してください' : ''}
                      />
                    )}
                  />
                  <FormControl size="small" disabled={skill.isDeleted}>
                    <InputLabel>レベル</InputLabel>
                    <Select
                      value={skill.skill_level}
                      label="レベル"
                      onChange={(e) => updateEditSkill(index, 'skill_level', e.target.value)}
                    >
                      <MenuItem value="beginner">初級</MenuItem>
                      <MenuItem value="intermediate">中級</MenuItem>
                      <MenuItem value="advanced">上級</MenuItem>
                      <MenuItem value="expert">エキスパート</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="経験年数"
                    type="number"
                    value={skill.years_experience || ''}
                    onChange={(e) => updateEditSkill(index, 'years_experience', parseInt(e.target.value) || 0)}
                    size="small"
                    inputProps={{ min: 0, max: 50 }}
                    disabled={skill.isDeleted}
                  />
                  <IconButton
                    color={skill.isDeleted ? "default" : "error"}
                    onClick={() => removeEditSkill(index)}
                    size="small"
                  >
                    <RemoveCircle />
                  </IconButton>
                </Box>
              ))}
              
              {editSkillsForm.filter(skill => !skill.isDeleted).length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                  業務内容・使用言語等が登録されていません。「業務・言語追加」ボタンで追加してください。
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSaveEngineer}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EngineerDetail;